import React, { useCallback, useMemo } from 'react';
import { ModalComponentType } from 'store/reducers/modals/types';
import { closeConfirmationModalAction, openConfirmationModalAction } from 'store/reducers/modals/actions';
import { useAppDispatch } from 'store';
import {
  deleteByIdDraftProjectAction,
  deleteByIdFlowProjectAction,
  loadMoveFlowAction,
  moveProjectAction,
} from 'store/reducers/projectManager/actions';
import { useSelector } from 'react-redux';
import { getFlowDraftInfo, getFlowsAndDraft } from 'store/reducers/projectManager/getters';
import { FlowType } from 'components/projectsManager/Modals/MoveProjectModal/types';
import { MoveProjectModalUI } from 'modules/ui/ManagerOrAdminComponentsUI/ModalsUI/MoveProjectModalUI';
import Snackbar from 'services/Snackbar';

interface CopyProjectModalProps {
  projectId: string;
  projectName: string;
  activeFlowId: string;
}

export const MoveProjectModal: ModalComponentType<CopyProjectModalProps> = ({
  onClose,
  projectId,
  activeFlowId,
  projectName,
}) => {
  const dispatch = useAppDispatch();

  const flowsAndDraft = useSelector(getFlowsAndDraft);
  const flowDraftInfo = useSelector(getFlowDraftInfo);
  const flowDraftInfoId = flowDraftInfo?.id;

  const flowsItems = useMemo(() => {
    return flowsAndDraft?.reduce<FlowType[]>((acc, flow) => {
      if (flow?.id !== activeFlowId) {
        acc.push({
          name: flow?.id || '',
          value: flow?.name || '',
        });
      }
      return acc;
    }, []);
  }, [flowsAndDraft, activeFlowId]);

  const isDraftFlow = activeFlowId === flowDraftInfoId;

  const onMoveProject = useCallback(
    async (selectFlowsName: string) => {
      try {
        const moveProjectRes = await dispatch(moveProjectAction({ projectId, flowId: selectFlowsName })).unwrap();

        if (moveProjectRes) {
          !isDraftFlow ? dispatch(deleteByIdFlowProjectAction(projectId)) : dispatch(deleteByIdDraftProjectAction(projectId));
          dispatch(closeConfirmationModalAction());
          onClose();
        }
      } catch (error) {
        console.error('Error creating project version:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, isDraftFlow, onClose],
  );

  const onMoveProjectModal = useCallback(
    async ({ value, name }: FlowType) => {
      if (!name) {
        return Snackbar.show('Поле «Поток» обязательно для заполнения', 'error');
      }

      const moveFLowActions = await dispatch(loadMoveFlowAction(name)).unwrap();
      const hasName = moveFLowActions?.find(({ title }) => title === projectName);

      if (hasName) {
        return Snackbar.show('В потоке уже есть проект с таким названием. Измените название и повторите попытку', 'error');
      }

      dispatch(
        openConfirmationModalAction({
          confirmationButtonText: 'Да',
          cancelButtonText: 'Нет',
          subTitleText: `Действительно переместить проект «${projectName}» в поток «${value}»?`,
          onConfirm: () => onMoveProject(name),
          titleText: 'Перемещение проекта',
          width: '320px',
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onMoveProject, projectName],
  );

  return <MoveProjectModalUI onMoveProjectModal={onMoveProjectModal} onClose={onClose} flowsItems={flowsItems} />;
};
